import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { CachedOutlined } from "@mui/icons-material"
import PageHeader from "../../components/PageHeader/PageHeader"
import { InputAdornment, Stack, TextField } from "@mui/material"
import { CustomerTable } from "./CustomerTable"
import { useLocation, useNavigate } from "react-router-dom"
import queryString from "query-string"
import { GridSortModel } from "@mui/x-data-grid"
import { ROOT } from "../../constants/path"
import { StyledBox, StyledButton } from "../Transactions/styles"
import { GridParent } from "../BankAccounts/styles"
import { Search } from "@mui/icons-material"
import { useEffect, useMemo } from "react"
import {
  fetchCustomersRequest,
  selectCustomerState,
} from "../../slices/customerSlice"
import { debounce } from "lodash"
import { CustomerContainer, CustomerFilterWrapper } from "./styles"
import { getParam } from "../../utils/customerHelpers"
import { PaginationType } from "../../models/customer.model"

export const Customers = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { customers, loading, pageInfo } = useAppSelector(selectCustomerState)

  const params = useMemo(
    () => queryString.parse(location.search),
    [location.search],
  )

  const filters = {
    customer: getParam(params.customer) ?? "",
    sortBy: getParam(params.sortBy),
    orderBy: getParam(params.orderBy),
    status: getParam(params.status) ?? "active",
  }

  const pagination = {
    page: Number(getParam(params.page) ?? 0),
    pageSize: Number(getParam(params.pageSize) ?? 10),
  }

  const updateURL = (next: Record<string, any>) => {
    navigate({
      search: queryString.stringify({
        ...params,
        ...next,
      }),
    })
  }

  useEffect(() => {
    dispatch(
      fetchCustomersRequest({
        ...filters,
        ...pagination,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, filters.sortBy, filters.orderBy])

  const debouncedFetch = useMemo(
    () =>
      debounce((payload) => {
        dispatch(fetchCustomersRequest(payload))
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    debouncedFetch({
      ...filters,
      ...pagination,
      page: 0,
    })

    return () => debouncedFetch.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.customer])

  const onCustomerSearch = (value: string) =>
    updateURL({ customer: value, page: 0 })

  const setPaginationModel = (page: PaginationType) =>
    updateURL({ page: page.page, pageSize: page.pageSize })

  const handleSortModelChange = (model: GridSortModel) => {
    if (!model[0]) return
    updateURL({
      sortBy: model[0].field,
      orderBy: model[0].sort,
      page: 0,
    })
  }

  const handleRefresh = () =>
    dispatch(
      fetchCustomersRequest({
        ...filters,
        ...pagination,
      }),
    )

  return (
    <GridParent>
      <PageHeader
        title="Customers"
        breadcrums={[{ title: "Home", path: ROOT }, { title: "Customers" }]}
      />
      <CustomerContainer sx={{ display: "flex", flexDirection: "row" }}>
        <Stack spacing={2} sx={{ height: "100%", width: "100%" }}>
          <StyledBox display="flex" gap={2}>
            <CustomerFilterWrapper>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search by Customers..."
                value={filters.customer}
                onChange={(e) => onCustomerSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </CustomerFilterWrapper>
            <div>
              <StyledButton
                variant="outlined"
                startIcon={<CachedOutlined fontSize="small" />}
                color="inherit"
                onClick={handleRefresh}
                sx={{ minWidth: "100px" }}
              >
                <div>Refresh </div>
              </StyledButton>
            </div>
          </StyledBox>

          <CustomerTable
            customers={customers}
            isLoading={loading}
            pageInfo={pageInfo}
            paginationModel={pagination}
            setPaginationModel={setPaginationModel}
            onSortModelChange={handleSortModelChange}
          />
        </Stack>
      </CustomerContainer>
    </GridParent>
  )
}
